import { memo, useCallback, useMemo, useState } from "react";
import Accordion from "../../../components/accordian";
import { useTranslation } from "react-i18next";
import { Controller } from "react-hook-form";
import TextInput from "../../../components/inputs/TextInput";
import CheckboxInput from "../../../components/inputs/CheckboxInput";
import ComboboxInput from "../../../components/inputs/ComboboxInput";
import { mapToOptions } from "../../../lib/functions";
import { useGetAllHosts } from "../../../hooks/common/query";
import FileUploadInput from "../../../components/inputs/FileUploadInput";
import {
  allowedFileTypes,
  ContentType,
  WAREHOUSE_IN_OUT_PERMIT_ATTACHMENT_KEYS,
  WAREHOUSE_IN_OUT_PERMIT_ATTACHMENTS_TYPE,
  WarehouseInOutPermitRequestDetails,
} from "../../../constants/common";
import toast from "react-hot-toast";
import ViewAttachmentModal from "../../../components/modal/ViewAttachmentModal";
import { useGetWarehouseInOutPermitFileByType } from "../../../hooks/permit/mutation";

interface PermitDetailsProps {
  control: any;
  errors: any;
  clearErrors: any;
  isAttachments: {
    haveEscortingEmployeeId: number;
  };
  uploadRefs: Record<string, React.MutableRefObject<HTMLInputElement | null>>;
  fileNames: Record<string, string>;
  setFileName: (key: string, value: React.SetStateAction<string>) => void;

  generalDescription: number;
  setGeneralDescription: React.Dispatch<React.SetStateAction<number>>;

  generalDescriptionOptions: {
    id: number;
    label: string;
  }[];

  permitDetails?: WarehouseInOutPermitRequestDetails;

  isFieldsDisabled: boolean;
}

const PermitDetails = ({
  control,
  errors,
  clearErrors,
  isAttachments,
  uploadRefs,
  fileNames,
  setFileName,
  generalDescription,
  setGeneralDescription,
  generalDescriptionOptions,
  permitDetails,
  isFieldsDisabled,
}: PermitDetailsProps) => {
  const { t } = useTranslation();

  const [selectedKey, setSelectedKey] = useState<string>("1");

  const { data: hosts } = useGetAllHosts();

  const escorts = useMemo(
    () => mapToOptions("fullName", "fullName", "user_id", hosts),
    [hosts],
  );

  const [previewFiles, setPreviewFiles] = useState<
    Record<number, { url: string; contentType: string }>
  >({});
  const [imageSrc, setImageSrc] = useState("");
  const [contentType, setContentType] = useState("");
  const [isViewAttachmentModalOpen, setIsViewAttachmentModalOpen] =
    useState(false);
  const [selectedType, setSelectedType] = useState<number>(0);
  const [isGetFileByTypeLoading, setIsGetFileByTypeLoading] = useState(false);

  const {
    mutateAsync: GetWarehouseInOutPermitFileByType,
    isPending: isGetWarehouseInOutPermitFileByTypeLoading,
  } = useGetWarehouseInOutPermitFileByType();

  const handleAttachmentView = useCallback(
    async (type: number) => {
      try {
        // If we already have a local preview for this attachment type
        if (previewFiles && previewFiles[type]) {
          setImageSrc(previewFiles[type].url);
          setContentType(previewFiles[type].contentType);
          setIsViewAttachmentModalOpen(true);
          return;
        }

        if (permitDetails?.id) {
          setSelectedType(type);

          const body = {
            Id: permitDetails?.id,
            Type: type,
          };

          const res = await GetWarehouseInOutPermitFileByType(body);

          setIsGetFileByTypeLoading(isGetWarehouseInOutPermitFileByTypeLoading);

          if (res) {
            let url = "";
            if (res?.contentType !== ContentType.PDF) {
              url = `data:${res?.contentType};base64,${res?.photoFile}`;
            } else {
              const binaryString = atob(res?.photoFile);
              const byteArray = new Uint8Array(binaryString.length);

              for (let i = 0; i < binaryString.length; i++) {
                byteArray[i] = binaryString.charCodeAt(i);
              }

              const blob = new Blob([byteArray], { type: ContentType.PDF });
              url = URL.createObjectURL(blob);
            }
            setImageSrc(url);
            setContentType(res?.contentType);
            setIsViewAttachmentModalOpen(true);
          }
        }
      } catch (error) {
        toast.error("Something went wrong");
      } finally {
        setSelectedType(0);
      }
    },
    [
      previewFiles,
      permitDetails?.id,
      GetWarehouseInOutPermitFileByType,
      isGetWarehouseInOutPermitFileByTypeLoading,
    ],
  );

  return (
    <>
      <div className="bg-white z-10 p-2 md:p-4 rounded-xl flex flex-col gap-3 lg:gap-6 shadow-[0px_1px_5px_0px_rgba(113,112,163,0.3)]">
        <Accordion
          id="1"
          selectedKey={selectedKey}
          onSelectionChange={setSelectedKey}
          title={
            <h1 className="text-xl md:text-2xl flex flex-1 -mt-2 items-center">
              {t("1")}. {t("permitDetails")}
            </h1>
          }
        >
          <div className="flex flex-col gap-2 lg:gap-4">
            <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 lg:gap-4">
              <Controller
                name="exitFromBuilding"
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <TextInput
                    label={t("exitFromBuilding")}
                    value={value}
                    onChange={onChange}
                    error={errors?.[name]?.message}
                    required
                    readOnly={isFieldsDisabled}
                  />
                )}
              />

              <Controller
                name="entryIntoBuilding"
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <TextInput
                    label={t("entryIntoBuilding")}
                    value={value}
                    onChange={onChange}
                    error={errors?.[name]?.message}
                    required
                    readOnly={isFieldsDisabled}
                  />
                )}
              />

              <Controller
                name="escortEmployee"
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <ComboboxInput
                    options={escorts}
                    value={
                      escorts?.find((item) => item.value === Number(value))
                        ?.labelEn || ""
                    }
                    onChange={(value) => {
                      onChange(value);
                      clearErrors(name);
                    }}
                    error={errors?.[name]?.message}
                    label={t("escortingEmployee")}
                    required
                    readOnly={
                      isFieldsDisabled
                      // user?.n_RoleID !== USER_ROLES_IDS.ADMIN ||
                      // (user?.n_RoleID === USER_ROLES_IDS.ADMIN &&
                      //   location.pathname ===
                      //     APP_ROUTES.APPROVE_WAREHOUSE_IN_OUT_PERMIT)
                    }
                  />
                )}
              />

              <FileUploadInput
                name="uploadEscortingEmployeeIDCard"
                label={t("uploadEscortingEmployeeIDCard")}
                accept=".png,.jpg,.jpeg,.pdf"
                allowedTypes={allowedFileTypes}
                isAttachments={isAttachments?.haveEscortingEmployeeId}
                disabled={isFieldsDisabled}
                control={control}
                uploadRef={
                  uploadRefs[
                    WAREHOUSE_IN_OUT_PERMIT_ATTACHMENT_KEYS
                      .ESCORTING_EMPLOYEE_ID_CARD
                  ]
                }
                fileName={
                  fileNames[
                    WAREHOUSE_IN_OUT_PERMIT_ATTACHMENT_KEYS
                      .ESCORTING_EMPLOYEE_ID_CARD
                  ]
                }
                setFileName={(val) =>
                  setFileName(
                    WAREHOUSE_IN_OUT_PERMIT_ATTACHMENT_KEYS.ESCORTING_EMPLOYEE_ID_CARD,
                    val,
                  )
                }
                handleAttachmentView={handleAttachmentView}
                selectedType={selectedType}
                attachmentType={
                  WAREHOUSE_IN_OUT_PERMIT_ATTACHMENTS_TYPE.ESCORTING_EMPLOYEE_ID_CARD
                }
                isGetFileByTypeLoading={isGetFileByTypeLoading}
                error={errors.uploadEscortingEmployeeIDCard}
                previewFiles={previewFiles}
                setPreviewFiles={setPreviewFiles}
                isRemovable={false}
              />
            </div>

            <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 lg:gap-4">
              <div className="col-span-2 relative flex flex-col gap-[14px]">
                <div className="relative flex items-center gap-2">
                  <label className="text-[13px] font-normal">
                    {t("generalDescription")}{" "}
                    {<span className="text-red-500">*</span>}
                  </label>
                </div>
                <div className="flex items-center gap-3 justify-between">
                  {generalDescriptionOptions?.map((option: any) => (
                    <div key={option.id}>
                      <CheckboxInput
                        id={String(option.id)}
                        label={option.label}
                        labelStyles="text-sm"
                        checked={generalDescription === option.id}
                        setChecked={() => setGeneralDescription(option.id)}
                        readOnly={isFieldsDisabled}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {generalDescription ===
                generalDescriptionOptions.find(
                  (opt: { label: string }) => opt.label === "Other",
                )?.id && (
                <Controller
                  name="generalDescriptionOther"
                  control={control}
                  render={({ field: { onChange, name, value } }) => (
                    <TextInput
                      label={t("generalDescriptionOther")}
                      value={value}
                      onChange={onChange}
                      error={errors?.[name]?.message}
                      required
                      readOnly={isFieldsDisabled}
                    />
                  )}
                />
              )}
            </div>
          </div>
        </Accordion>
      </div>

      <ViewAttachmentModal
        attachment={imageSrc}
        contentType={contentType}
        isOpen={isViewAttachmentModalOpen}
        onOpenChange={() => {
          setIsViewAttachmentModalOpen(false);
          setImageSrc("");
        }}
      />
    </>
  );
};

export default memo(PermitDetails);
